'use strict';

import axios from 'axios';
import {
  customEvent,
  setHtmlScrollable,
  setHtmlUnscrollable
} from '@jspartials/general-utils';
import { listenDOMEvent, dispatchCustomEvent } from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';
import { initPickUpStoresLocator } from '@jscomponents/pick-up-stores-locator';
import { initLoginAndSignUp } from '@jscomponents/login-and-sign-up';

// Selectors
const selector = {
  sidebar: {
    wrapper: '.js-sidebar-wrapper',
    drawer: '.js-sidebar-drawer',
    content: '.js-sidebar-content',
    open: '.js-sidebar-open',
    close: '.js-sidebar-close'
  }
};

// Elements
const element = {
  sidebar: {
    wrapper: null,
    drawer: null,
    content: null,
    open: [],
    close: []
  }
};

// Variables
const sidebarTypes = {
  pickUpStores: 'stores'
};
const sidebarEnvironments = {
  pdp: 'pdp'
};

// Functions after open sidebar that need to be initialized
const functionAfterOpenSidebar = {
  initPickUpStoresLocator: initPickUpStoresLocator,
  initLoginAndSignUp: initLoginAndSignUp
};

/**
 * Initialize the sidebar component
 * @returns {void}
 */
const initSidebar = () => {
  initElements();

  listenDOMEvent(document, customEvent.sidebar.close, closeSidebar);
};

/**
 * Initialize the sidebar elements
 * @returns {void}
 */
const initElements = () => {
  element.sidebar.wrapper =
    document.querySelector(selector.sidebar.wrapper) || null;
  element.sidebar.drawer =
    document.querySelector(selector.sidebar.drawer) || null;
  element.sidebar.content =
    document.querySelector(selector.sidebar.content) || null;

  setLoadingDrawer();
  listenOpenElements();
  listenCloseElements();
};

/**
 * Listen for open elements to open the sidebar
 * @returns {void}
 */
const listenOpenElements = () => {
  element.sidebar.open =
    Array.from(document.querySelectorAll(selector.sidebar.open)) || [];

  element.sidebar.open.forEach((element) => {
    listenDOMEvent(element, 'click', openSidebarByClick);
  });
};

/**
 * Listen for close elements to close the sidebar
 * @returns {void}
 */
const listenCloseElements = () => {
  element.sidebar.close =
    Array.from(document.querySelectorAll(selector.sidebar.close)) || [];

  element.sidebar.close.forEach((element) => {
    listenDOMEvent(element, 'click', closeSidebar);
  });
};

/**
 * Set the loading class to the sidebar drawer
 * @returns {void}
 */
const setLoadingDrawer = () => {
  if (element.sidebar.drawer) {
    addClassToElement(element.sidebar.drawer, classes.is.loading);
  }
};

/**
 * Unset the loading class to the sidebar drawer
 * @returns {void}
 */
const unsetLoadingDrawer = () => {
  if (element.sidebar.drawer) {
    removeClassToElement(element.sidebar.drawer, classes.is.loading);
  }
};

/**
 * Open the sidebar by click on an element
 * @param {Event} event - Event object from the click
 * @returns {void}
 */
const openSidebarByClick = (event) => {
  const elementClicked = event.target;
  const trigger = elementClicked.closest(selector.sidebar.open);
  const sidebarDrawer = trigger?.dataset?.sidebarDrawer;
  const sidebarEnvironment = trigger?.dataset?.sidebarEnvironment;

  openSidebar({
    drawer: sidebarDrawer,
    environment: sidebarEnvironment,
    trigger: trigger
  });
};

/**
 * Open the sidebar
 * @param {Object} data - Data to open the sidebar
 * @returns {void}
 */
const openSidebar = ({ drawer = '', environment = '', trigger = null }) => {
  const currentSidebarDrawer = element.sidebar.wrapper?.dataset?.drawer;
  const forceReloadSidebar =
    environment === sidebarEnvironments.pdp &&
    drawer === sidebarTypes.pickUpStores;

  setVisibleSidebar();

  if (forceReloadSidebar || drawer !== currentSidebarDrawer) {
    const info = getExtraInfo(drawer, trigger);

    info.drawer = drawer;
    info.environment = environment;
    loadSidebarDrawer(info);
  }
};

/**
 * Set the sidebar visible
 * @returns {void}
 */
const setVisibleSidebar = () => {
  if (element.sidebar.wrapper) {
    setHtmlUnscrollable();
    unsetLoadingDrawer();
    addClassToElement(element.sidebar.wrapper, classes.is.visible);
  }
};

/**
 * Close the sidebar
 * @returns {void}
 */
const closeSidebar = () => {
  if (element.sidebar.wrapper) {
    dispatchCustomEvent(
      globalVariables.customEvent.forceCloseSessionAutocompleteStoresLocator
    );
    setHtmlScrollable();
    removeClassToElement(element.sidebar.wrapper, classes.is.visible);
  }
};

/**
 * Get extra info for the sidebar
 * @param {String} drawer - Drawer name
 * @param {HTMLElement} trigger - Element that triggers the sidebar
 * @returns {Object} - Extra info for the sidebar
 */
const getExtraInfo = (drawer, trigger) => {
  let extraInfo = {};

  if (trigger) {
    switch (drawer) {
      case sidebarTypes.pickUpStores:
        extraInfo.productId = trigger?.dataset?.productId;
        break;
      default:
        break;
    }
  }

  return extraInfo;
};

/**
 * Load the sidebar drawer
 * @param {Object} info - Info to load the sidebar drawer
 * @returns {void}
 */
const loadSidebarDrawer = (info) => {
  setNameToSidebarDrawer(info.drawer);
  setLoadingDrawer();

  axios
    .get(globalVariables.endpoint.sidebarShow, {
      params: info
    })
    .then((response) => {
      const { data = {} } = response;
      const { htmlContent = '' } = data;
      const { functions = [] } = data;

      unsetLoadingDrawer();
      paintContent(htmlContent);
      listenCloseElements();
      initFunctionsAfterOpenSidebar(functions);
    })
    .catch((error) => {
      console.error('Error:', error);
    });
};

/**
 * Paint the content of the sidebar
 * @param {String} htmlContent - HTML content to paint
 * @returns {void}
 */
const paintContent = (htmlContent) => {
  if (element.sidebar.content) {
    element.sidebar.content.innerHTML = htmlContent;
  }
};

/**
 * Set the name to the sidebar drawer
 * @param {String} drawerName - Name of the drawer
 * @returns {void}
 */
const setNameToSidebarDrawer = (drawerName) => {
  if (element.sidebar.wrapper) {
    element.sidebar.wrapper.dataset.drawer = drawerName;
  }
};

/**
 * Initialize the functions after open the sidebar
 * @param {Array} functions - Functions to initialize
 * @returns {void}
 */
const initFunctionsAfterOpenSidebar = (functions) => {
  functions.forEach((functionName) => {
    if (functionAfterOpenSidebar[functionName]) {
      functionAfterOpenSidebar[functionName]();
    }
  });
};

// Initialize the component when the DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  initSidebar();
});

// Listen for custom event to initialize the component again
document.addEventListener(customEvent.sidebar.init, () => {
  initSidebar();
});

// Listen for custom event to open the sidebar
document.addEventListener(customEvent.sidebar.open, (event) => {
  const { detail = {} } = event;

  openSidebar(detail);
});
